import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { format, startOfWeek, endOfWeek, startOfDay, endOfDay } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Phone, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { EditDormitoryMotherInline } from "./EditDormitoryMotherInline";

// Define types for better type safety
interface DormitoryMotherInfo {
  dormitory: string;
  mother: string;
  contact: string;
  visitCount: number;
}
interface StudentVisit {
  id: string;
  student_id: string;
  complaint: string;
  diagnosis: string;
  treatment: string;
  temperature: number;
  date: string;
  students: {
    id: string;
    surname: string;
    other_names: string;
    class: string;
    stream: string;
    dormitory: string;
    dormitory_mother: string;
    dormitory_mother_contact: string;
  } | null;
}
export const DailyVisitsList = ({
  date = new Date()
}: {
  date?: Date;
}) => {
  const {
    user
  } = useAuth();
  const [selectedDate] = useState(date);
  const [dormMotherDialogOpen, setDormMotherDialogOpen] = useState(false);
  const formattedDate = format(selectedDate, 'yyyy-MM-dd');

  // Get current academic year and term for consistent filtering
  const {
    data: currentPeriod
  } = useQuery({
    queryKey: ['currentPeriod'],
    queryFn: async () => {
      console.log('Fetching current period...');
      const {
        data: activeYear,
        error: yearError
      } = await supabase.from('academic_years').select('*, terms(*)').eq('is_active', true).single();
      if (yearError) {
        console.error('Error fetching academic year:', yearError);
        throw yearError;
      }
      const activeTerm = activeYear?.terms?.find((term: any) => term.is_active);
      console.log('Current period fetched:', {
        activeYear,
        activeTerm
      });
      return {
        academicYear: activeYear,
        term: activeTerm
      };
    }
  });

  // Calculate date ranges - matching Reports.tsx logic exactly
  const getDashboardDateRanges = () => {
    const now = new Date();

    // Daily range
    const dailyStart = startOfDay(now);
    const dailyEnd = endOfDay(now);

    // Weekly range - using same logic as Reports.tsx
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6); // End of week (Saturday)

    // If we have active term, constrain weekly range to term dates
    let finalWeekStart = weekStart;
    let finalWeekEnd = weekEnd;
    if (currentPeriod?.term) {
      const termStart = new Date(currentPeriod.term.start_date);
      const termEnd = new Date(currentPeriod.term.end_date);
      finalWeekStart = weekStart < termStart ? termStart : weekStart;
      finalWeekEnd = weekEnd > termEnd ? termEnd : weekEnd;
    }
    console.log('Dashboard date ranges calculated:', {
      daily: {
        start: dailyStart.toISOString(),
        end: dailyEnd.toISOString()
      },
      weekly: {
        start: finalWeekStart.toISOString(),
        end: finalWeekEnd.toISOString()
      },
      termConstrained: !!currentPeriod?.term
    });
    return {
      daily: {
        start: dailyStart,
        end: dailyEnd
      },
      weekly: {
        start: finalWeekStart,
        end: finalWeekEnd
      }
    };
  };

  // Dashboard visit counts with consistent filtering
  const {
    data: dashboardCounts,
    isLoading: isDashboardLoading
  } = useQuery({
    queryKey: ['dashboard-visit-counts', currentPeriod?.academicYear?.id, currentPeriod?.term?.id],
    queryFn: async () => {
      if (!user?.schoolId || !currentPeriod?.academicYear || !currentPeriod?.term) {
        console.log('Missing required data for dashboard counts');
        return {
          todayVisits: 0,
          weeklyVisits: 0,
          termVisits: 0
        };
      }
      const ranges = getDashboardDateRanges();
      console.log('=== DASHBOARD VISIT COUNTS QUERY ===');
      console.log('School ID:', user.schoolId);
      console.log('Academic Year ID:', currentPeriod.academicYear.id);
      console.log('Term ID:', currentPeriod.term.id);

      // Today's visits
      const {
        data: todayVisits,
        error: todayError
      } = await supabase.from('visits').select('id').eq('school_id', user.schoolId).eq('academic_year_id', currentPeriod.academicYear.id).eq('term_id', currentPeriod.term.id).gte('date', ranges.daily.start.toISOString()).lte('date', ranges.daily.end.toISOString());
      if (todayError) {
        console.error('Error fetching today visits:', todayError);
        throw todayError;
      }

      // Weekly visits - using exact same logic as Reports.tsx
      const {
        data: weeklyVisits,
        error: weeklyError
      } = await supabase.from('visits').select('id').eq('school_id', user.schoolId).eq('academic_year_id', currentPeriod.academicYear.id).eq('term_id', currentPeriod.term.id).gte('date', ranges.weekly.start.toISOString()).lte('date', ranges.weekly.end.toISOString());
      if (weeklyError) {
        console.error('Error fetching weekly visits:', weeklyError);
        throw weeklyError;
      }

      // Term visits
      const {
        data: termVisits,
        error: termError
      } = await supabase.from('visits').select('id').eq('school_id', user.schoolId).eq('academic_year_id', currentPeriod.academicYear.id).eq('term_id', currentPeriod.term.id);
      if (termError) {
        console.error('Error fetching term visits:', termError);
        throw termError;
      }
      const counts = {
        todayVisits: todayVisits?.length || 0,
        weeklyVisits: weeklyVisits?.length || 0,
        termVisits: termVisits?.length || 0
      };
      console.log('=== DASHBOARD COUNTS RESULT ===');
      console.log('Today visits:', counts.todayVisits);
      console.log('Weekly visits:', counts.weeklyVisits);
      console.log('Term visits:', counts.termVisits);
      return counts;
    },
    enabled: !!user?.schoolId && !!currentPeriod?.academicYear && !!currentPeriod?.term
  });

  // Create start and end of day for the selected date
  const dayStart = new Date(selectedDate);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(selectedDate);
  dayEnd.setHours(23, 59, 59, 999);

  // Convert to ISO strings for the query
  const startISO = dayStart.toISOString();
  const endISO = dayEnd.toISOString();

  // Modified query to fetch boarding students and their visit details for the selected date
  const {
    data: boardingStudentVisits,
    isLoading: isStudentsLoading
  } = useQuery({
    queryKey: ['boarding-student-visits-details', formattedDate],
    queryFn: async () => {
      if (!user?.schoolId) return [];

      // Fetch visits for boarding students on the selected date
      const {
        data: visits,
        error: visitsError
      } = await supabase.from('visits').select(`
          id,
          student_id,
          complaint,
          diagnosis,
          treatment,
          temperature,
          date,
          students:student_id (
            id,
            surname,
            other_names,
            class,
            stream,
            dormitory,
            dormitory_mother,
            dormitory_mother_contact
          )
        `).eq('school_id', user.schoolId).gte('date', startISO).lte('date', endISO).not('student_id', 'is', null).not('students.dormitory', 'is', null);
      if (visitsError) {
        console.error('Error fetching boarding student visits:', visitsError);
        throw visitsError;
      }

      // Filter out visits where the student doesn't have dormitory info
      return visits.filter(visit => visit.students?.dormitory) as StudentVisit[];
    },
    enabled: !!user?.schoolId
  });

  // Get only dormitory mothers who have students that visited today
  const {
    data: todaysDormMothers,
    isLoading: isDormMothersLoading
  } = useQuery({
    queryKey: ['todays-dorm-mothers', formattedDate],
    queryFn: async () => {
      if (!user?.schoolId || !boardingStudentVisits?.length) return [];

      // Extract unique dormitory information from today's boarding student visits
      const dormitoryData = boardingStudentVisits.reduce<Record<string, {
        dormitory: string;
        mother: string;
        contact: string;
        visitCount: number;
        uniqueStudentIds: Set<string>;
      }>>((acc, visit) => {
        if (!visit.students) return acc;
        const dormKey = visit.students.dormitory || 'Unknown';
        if (!acc[dormKey]) {
          acc[dormKey] = {
            dormitory: dormKey,
            mother: visit.students.dormitory_mother || 'Not assigned',
            contact: visit.students.dormitory_mother_contact || 'No contact',
            visitCount: 0,
            uniqueStudentIds: new Set()
          };
        }

        // Count visits and unique students
        acc[dormKey].visitCount++;
        acc[dormKey].uniqueStudentIds.add(visit.student_id);
        return acc;
      }, {});

      // Convert to array and calculate unique student count
      return Object.values(dormitoryData).map(dorm => ({
        dormitory: dorm.dormitory,
        mother: dorm.mother,
        contact: dorm.contact,
        visitCount: dorm.visitCount
      })) as DormitoryMotherInfo[];
    },
    enabled: !!user?.schoolId && !!boardingStudentVisits
  });

  // Group visits by dormitory
  const dormitoryGroups = boardingStudentVisits?.reduce((groups, visit) => {
    if (!visit.students) return groups;
    const dormitory = visit.students.dormitory || 'Unknown';
    if (!groups[dormitory]) {
      groups[dormitory] = {
        mother: visit.students.dormitory_mother || 'Not assigned',
        motherContact: visit.students.dormitory_mother_contact || 'No contact provided',
        visits: []
      };
    }
    groups[dormitory].visits.push(visit);
    return groups;
  }, {} as Record<string, {
    mother: string;
    motherContact: string;
    visits: typeof boardingStudentVisits;
  }>) || {};
  return <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle>Visit Statistics</CardTitle>
            <CardDescription>
              Dispensary visits on {format(selectedDate, 'MMMM d, yyyy')}
            </CardDescription>
          </div>
          <Button onClick={() => setDormMotherDialogOpen(true)} variant="outline" className="gap-2" disabled={!(todaysDormMothers?.length > 0)}>
            <Users size={16} />
            View Today's Dorm Mothers
          </Button>
        </CardHeader>
        <CardContent>
          {isDashboardLoading ? <div className="grid grid-cols-3 gap-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div> : <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {dashboardCounts?.todayVisits || 0}
                </div>
                <div className="text-sm text-muted-foreground">Today's Visits</div>
              </div>
              <div className="text-center">
                
                
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {dashboardCounts?.termVisits || 0}
                </div>
                <div className="text-sm text-muted-foreground">Term 2 Visits</div>
              </div>
            </div>}
        </CardContent>
      </Card>

      <div className="space-y-6">
        <h2 className="text-2xl font-semibold">Boarding Students Treated Today</h2>
        
        {isStudentsLoading ? <div className="space-y-4">
            <Skeleton className="h-52 w-full" />
            <Skeleton className="h-52 w-full" />
          </div> : Object.keys(dormitoryGroups).length === 0 ? <Card>
            <CardContent className="py-6">
              <p className="text-center text-muted-foreground">No boarding students visited the dispensary today</p>
            </CardContent>
          </Card> : Object.entries(dormitoryGroups).map(([dormitory, data]) => <Card key={dormitory} className="overflow-hidden">
              <CardHeader className="bg-muted">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                  <div>
                    <CardTitle>{dormitory} Dormitory</CardTitle>
                    <CardDescription>Total Visits: {data.visits.length}</CardDescription>
                  </div>
                  <div className="text-sm flex items-start gap-2">
                    <div>
                      <p><span className="font-medium">Dormitory Mother:</span> {data.mother}</p>
                      <p><span className="font-medium">Contact:</span> {data.motherContact}</p>
                    </div>
                    <EditDormitoryMotherInline 
                      dormitory={dormitory}
                      currentMother={data.mother}
                      currentContact={data.motherContact}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Temperature</TableHead>
                        <TableHead>Complaint</TableHead>
                        <TableHead>Diagnosis</TableHead>
                        <TableHead>Treatment</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.visits.map(visit => <TableRow key={visit.id}>
                          <TableCell className="font-medium">
                            {visit.students?.surname} {visit.students?.other_names}
                          </TableCell>
                          <TableCell>
                            {visit.students?.class} {visit.students?.stream}
                          </TableCell>
                          <TableCell>{format(new Date(visit.date), 'HH:mm')}</TableCell>
                          <TableCell>{visit.temperature}°C</TableCell>
                          <TableCell className="max-w-[200px] truncate" title={visit.complaint}>
                            {visit.complaint}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate" title={visit.diagnosis}>
                            {visit.diagnosis}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate" title={visit.treatment}>
                            {visit.treatment}
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>)}
      </div>

      {/* Dormitory Mothers Dialog - Modified to show only mothers with students who visited today */}
      <Dialog open={dormMotherDialogOpen} onOpenChange={setDormMotherDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Today's Dormitory Mothers</DialogTitle>
            <DialogDescription>
              Dorm mothers with students who visited the dispensary today ({format(selectedDate, 'MMMM d, yyyy')})
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto pr-6 max-h-[calc(90vh-120px)] space-y-6">
            {isDormMothersLoading ? <div className="space-y-4">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div> : todaysDormMothers?.length === 0 ? <p className="text-center text-muted-foreground py-8">No dormitory mothers with student visits today</p> : <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dormitory</TableHead>
                      <TableHead>Mother's Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Student Visits</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {todaysDormMothers?.map((dorm, index) => <TableRow key={`${dorm.dormitory}-${index}`}>
                        <TableCell className="font-medium">
                          {dorm.dormitory}
                        </TableCell>
                        <TableCell>{dorm.mother}</TableCell>
                        <TableCell className="flex items-center gap-1">
                          <Phone size={14} className="text-muted-foreground" />
                          <span>{dorm.contact}</span>
                        </TableCell>
                        <TableCell>{dorm.visitCount}</TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>;
};